import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { produitsService } from '../services/api';
import { getErrorMessage } from '../utils/errorFormatter';
import { handleImageError, PLACEHOLDER_IMAGE } from '../utils/imageFallback';
import Drawer from './Drawer';

const CATEGORY_ICONS = {
  semences: '🌾',
  engrais: '🧪',
  materiel: '🔧',
  phyto: '🌿',
  irrigation: '💧',
  alimentation: '🍎',
};

const TOP_SEARCHES = [
  'Semences maïs',
  'Engrais NPK',
  'Pulvérisateur thermique',
  'Pack protection intégrée',
  'Pompe d’irrigation',
];

const SEASON_OPTIONS = [
  { value: '', label: 'Toutes les saisons' },
  { value: 'saison_des_pluies', label: 'Saison des pluies' },
  { value: 'saison_seche', label: 'Saison sèche' },
  { value: 'recolte', label: 'Récolte' },
];

const SORT_OPTIONS = [
  { value: '', label: 'Pertinence' },
  { value: 'prix_unitaire', label: 'Prix bas → haut' },
  { value: '-prix_unitaire', label: 'Prix haut → bas' },
  { value: '-en_vedette', label: 'Populaire' },
  { value: '-date_creation', label: 'Nouveautés' },
  { value: '-quantite_stock', label: 'Disponibilité' },
];

function getBadge(product) {
  if (product.en_vedette) return { text: 'Bestseller', variant: 'success' };
  if (product.stock_limite) return { text: 'Stock limité', variant: 'warning' };
  if (product.nouveau) return { text: 'Nouveau', variant: 'default' };
  if (product.est_disponible === false) return { text: 'Rupture', variant: 'danger' };
  return null;
}

function getProductIcon(product) {
  if (product.categorie_icone) return product.categorie_icone;
  const key = String(product.reference || '').split('-')[0]?.toLowerCase();
  return CATEGORY_ICONS[key] || '🛒';
}

function getProductImage(product) {
  return product.photo_principale || product.photo_2 || product.photo_3 || PLACEHOLDER_IMAGE;
}

function getPromotion(product) {
  const originalPrice = Number(product.prix_unitaire || product.price || 0);
  const promoPrice = Number(product.prix_promotion || product.prix_reduit || product.promotion_prix || 0);
  if (promoPrice > 0 && originalPrice > promoPrice) {
    return {
      originalPrice,
      promoPrice,
      percent: Math.round((1 - promoPrice / originalPrice) * 100),
    };
  }
  return null;
}

export default function Home({ onAdd }) {
  const [vedette, setVedette] = useState([]);
  const [produits, setProduits] = useState([]);
  const [categories, setCategories] = useState([]);
  const [offres, setOffres] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [filters, setFilters] = useState({
    categorie: '',
    disponible: '',
    nouveau: '',
    saison: '',
    ordering: '',
  });
  const [variete, setVariete] = useState('');
  const [priceRange, setPriceRange] = useState([0, 150000]);
  const [priceBounds, setPriceBounds] = useState([0, 150000]);
  const [page, setPage] = useState(1);
  const [nextPage, setNextPage] = useState(null);
  const [totalProductsCount, setTotalProductsCount] = useState(0);

  async function loadProducts(searchTerm = '', activeFilters = {}, pageNumber = 1) {
    const filtres = { ...activeFilters, variete, page: pageNumber };
    if (priceRange[0] > 0) filtres.prix_min = priceRange[0];
    if (priceRange[1] > 0) filtres.prix_max = priceRange[1];
    if (searchTerm) return produitsService.rechercher(searchTerm, filtres);
    return produitsService.getAll(filtres);
  }

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true);
      try {
        const [v, p, c, o, q] = await Promise.all([
          produitsService.getVedette(),
          loadProducts('', {}, 1),
          produitsService.getCategories(),
          produitsService.getOffresDuJour().catch(() => []),
          produitsService.getPacksConseils().catch(() => []),
        ]);
        if (mounted) {
          setVedette(Array.isArray(v) ? v : v.results || []);
          const productsData = Array.isArray(p) ? { results: p, count: p.length, next: null } : p;
          setProduits(productsData.results || []);
          setTotalProductsCount(productsData.count || productsData.results?.length || 0);
          setNextPage(productsData.next || null);
          setCategories(Array.isArray(c) ? c : c.results || []);
          setOffres(Array.isArray(o) ? o : o.results || []);
          setPacks(Array.isArray(q) ? q : q.results || []);
        }
      } catch (e) {
        setError(getErrorMessage(e));
      } finally {
        setLoading(false);
      }
    }
    load();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    const timer = setTimeout(async () => {
      try {
        setPage(1);
        setSearchLoading(true);
        setError(null);
        const p = await loadProducts(search.trim(), filters, 1);
        const productsData = Array.isArray(p) ? { results: p, count: p.length, next: null } : p;
        setProduits(productsData.results || []);
        setTotalProductsCount(productsData.count || productsData.results?.length || 0);
        setNextPage(productsData.next || null);
      } catch (e) {
        setError(getErrorMessage(e));
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [search, filters, variete, priceRange]);

  useEffect(() => {
    const prices = produits
      .map((p) => Number(p.prix_unitaire || 0))
      .filter((price) => price > 0);
    if (prices.length) {
      const bounds = [Math.min(...prices), Math.max(...prices)];
      setPriceBounds(bounds);
      setPriceRange((current) => {
        if (current[0] === 0 && current[1] === 150000) {
          return bounds;
        }
        const [currentMin, currentMax] = current;
        return [
          Math.max(bounds[0], currentMin),
          Math.min(bounds[1], currentMax),
        ];
      });
    }
  }, [produits]);

  const filteredProduits = produits.filter((produit) => {
    const prix = Number(produit.prix_unitaire || 0);
    const [minPrice, maxPrice] = priceRange;
    if (prix < minPrice || prix > maxPrice) return false;
    return true;
  });

  const searchEmpty = search.trim().length === 0;
  const noResults = !loading && search.trim().length > 0 && filteredProduits.length === 0;

  if (loading) return <div className="ag-container">Chargement…</div>;
  if (error) return (
    <div className="ag-container" style={{ textAlign: 'center', padding: '32px' }}>
      <h2>Oups, une erreur est survenue</h2>
      <p>{error}</p>
      <button
        type="button"
        className="ag-btn ag-btn--secondary"
        onClick={() => window.location.reload()}
      >
        Réessayer
      </button>
    </div>
  );

  const defaultCategories = [
    { id: 'semences', nom: 'Semences', icone: '🌱' },
    { id: 'engrais', nom: 'Engrais', icone: '🧪' },
    { id: 'materiel', nom: 'Matériel', icone: '🔧' },
    { id: 'phytosanitaires', nom: 'Phytosanitaires', icone: '🌿' },
  ];

  const visibleCategories = categories.length >= 4
    ? categories
    : defaultCategories;

  const offreDuJour = offres[0] || vedette[0] || produits[0];
  const totalProducts = totalProductsCount || produits.length;
  const totalOffers = offres.length;
  const totalPacks = packs.length > 0 ? packs.length : 3;
  const totalCategories = visibleCategories.length;
  const packsConseils = packs.length > 0 ? packs : [
    {
      titre: 'Pack démarrage semis',
      description: 'Semence, fertilisant starter et conseils d’utilisation pour un meilleur établissement de votre culture.',
      lien: '/client/espace',
    },
    {
      titre: 'Pack protection intégrée',
      description: 'Choix d’intrants sécurisés et support technique pour la protection des plantes en saison des pluies.',
      lien: '/client/espace',
    },
    {
      titre: 'Pack retour sur investissement',
      description: 'Combinaison d’engrais et d’outil de suivi pour limiter les pertes et améliorer le rendement.',
      lien: '/client/espace',
    },
  ];

  return (
    <div className="ag-home-page">
      {/* Mobile menu trigger */}
      <button
        className="ag-drawer-trigger"
        onClick={() => setIsDrawerOpen(true)}
        aria-label="Ouvrir le menu de navigation"
        type="button"
      >
        ☰
      </button>

      {/* Mobile drawer */}
      <Drawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)}>
        <div className="ag-home-panel">
          <h3>Rechercher</h3>
          <div className="ag-search ag-search--sidebar">
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Rechercher maïs, engrais, pulvérisateur..."
              aria-label="Recherche produits"
            />
            {searchLoading && <div className="ag-search-status">Recherche en cours…</div>}
            {search.trim().length > 1 && produits.length > 0 && (
              <ul className="ag-search-suggestions">
                {produits.slice(0,5).map(s => (
                  <li key={s.id}>
                    <Link to={`/produits/${s.id}`} onClick={() => setIsDrawerOpen(false)}>{s.nom}</Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="ag-home-panel">
          <h3>Filtres rapides</h3>
          <div className="ag-product-filters ag-product-filters--sidebar">
            <label>
              Trier par
              <select
                value={filters.ordering}
                onChange={(event) => setFilters((prev) => ({ ...prev, ordering: event.target.value }))}
              >
                <option value="-date_creation">Nouveautés</option>
                <option value="-en_vedette">Popularité</option>
                <option value="prix_unitaire">Prix croissant</option>
                <option value="-prix_unitaire">Prix décroissant</option>
                <option value="-quantite_stock">Disponibilité</option>
              </select>
            </label>
          </div>
        </div>

        <div className="ag-home-panel">
          <h3>Catégories</h3>
          <div className="ag-categories--sidebar">
            {visibleCategories.map((cat) => (
              <button
                key={cat.id}
                className={`ag-cat-card ${filters.categorie === cat.id ? 'ag-cat-card--active' : ''}`}
                onClick={() => {
                  setFilters((prev) => ({ ...prev, categorie: prev.categorie === cat.id ? '' : cat.id }));
                  setIsDrawerOpen(false);
                }}
                type="button"
              >
                <span className="ag-tile-icon">{cat.icone}</span>
                <span className="ag-cat-title">{cat.nom}</span>
              </button>
            ))}
          </div>
        </div>
      </Drawer>

      <section className="ag-home-hero">
        <div className="ag-home-hero-copy">
          <p className="ag-hero-label">AgroShop Mali 🌾</p>
          <h1>Vos intrants agricoles, livrés à temps</h1>
          <p className="ag-hero-text">Accédez à des semences certifiées, engrais de qualité et équipements agricoles modernes. Livraison rapide dans tout le Mali avec support technique gratuit.</p>
          <div className="ag-hero-actions">
            <Link className="ag-btn ag-btn--primary" to="/client/espace">Commencer maintenant</Link>
            <button
              type="button"
              className="ag-btn-secondary"
              onClick={() => setFilters((prev) => ({ ...prev, saison: 'saison_des_pluies' }))}
            >
              📅 Offres saisonnières
            </button>
          </div>
          <div className="ag-hero-trustline">
            <span>✓ Semences certifiées</span>
            <span>✓ Livraison 48-72h</span>
            <span>✓ Support gratuit</span>
          </div>
        </div>

        <div className="ag-home-hero-panel">
          <div className="ag-home-summary-cards">
            <div className="ag-home-summary-card">
              <span>Produits</span>
              <strong>{totalProducts}</strong>
            </div>
            <div className="ag-home-summary-card">
              <span>En promotion</span>
              <strong>{totalOffers}</strong>
            </div>
            <div className="ag-home-summary-card">
              <span>Packs conseils</span>
              <strong>{totalPacks}</strong>
            </div>
            <div className="ag-home-summary-card">
              <span>Catégories</span>
              <strong>{totalCategories}</strong>
            </div>
          </div>

          <div className="ag-home-offer-card">
            <div className="ag-home-offer-header">
              <span className="ag-home-offer-badge">⚡ Coup du jour</span>
              <h3>Offre flash</h3>
            </div>
            <p className="ag-home-offer-text">Profitez de réductions exclusives sur les produits les plus demandés cette semaine.</p>
            {offreDuJour ? (
              <div className="ag-home-offer-detail">
                <div>
                  <strong>{offreDuJour.produit_nom || offreDuJour.nom || offreDuJour.name}</strong>
                  <p>{offreDuJour.description || offreDuJour.description_courte || 'Sélection de qualité pour votre exploitation.'}</p>
                </div>
                <div className="ag-home-offer-price-block">
                  {offreDuJour.reduction_pourcentage !== undefined ? (
                    <>
                      <span className="ag-badge ag-badge--success">{offreDuJour.titre || `-${offreDuJour.reduction_pourcentage}%`}</span>
                      <span className="ag-home-offer-price">{offreDuJour.prix_reduit?.toLocaleString()} FCFA</span>
                    </>
                  ) : (
                    <>
                      <span className="ag-badge ag-badge--success">{offreDuJour.promotion_label || '-10%'}</span>
                      <span className="ag-home-offer-price">{offreDuJour.prix_promotion ? `${parseInt(offreDuJour.prix_promotion).toLocaleString()} FCFA` : '—'}</span>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <p>Chargement de l’offre du jour…</p>
            )}
            {offreDuJour && (
              <Link className="ag-btn ag-btn--primary ag-home-offer-action" to={`/produits/${offreDuJour.produit || offreDuJour.id}`}>Voir l'offre</Link>
            )}
          </div>
        </div>
      </section>

      <section className="ag-home-trust">
        <div className="ag-home-trust-grid">
          <div className="ag-home-trust-card">
            <strong>Livraison rapide</strong>
            <p>Réception prévue en 48 à 72 heures pour les commandes dans les zones principales.</p>
          </div>
          <div className="ag-home-trust-card">
            <strong>Qualité certifiée</strong>
            <p>Produits sélectionnés pour leur performance et leur résistance dans les cultures maliennes.</p>
          </div>
          <div className="ag-home-trust-card">
            <strong>Support local</strong>
            <p>Conseils pratiques et assistance client dédiée pour un achat serein.</p>
          </div>
        </div>
        <div className="ag-home-category-highlights">
          {visibleCategories.slice(0, 4).map((cat) => (
            <Link
              key={cat.id}
              to="#"
              className="ag-home-category-card"
              onClick={(event) => {
                event.preventDefault();
                setFilters((prev) => ({ ...prev, categorie: String(cat.id) }));
              }}
            >
              <div className="ag-home-category-icon">{cat.icone || CATEGORY_ICONS[cat.id] || '🌿'}</div>
              <div>
                <strong>{cat.nom || cat.name}</strong>
                <p>Découvrez nos meilleures offres pour {cat.nom || cat.name}.</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="ag-home-best-sellers">
        <div className="ag-section-header">
          <div>
            <h2>Meilleures ventes</h2>
            <p>Produits les plus appréciés par les agriculteurs maliens.</p>
          </div>
        </div>
        <div className="ag-products ag-products--compact">
          {vedette.slice(0, 4).map((p) => {
            const badge = getBadge(p);
            const promotion = getPromotion(p);
            const icon = getProductIcon(p);
            return (
              <article key={p.id} className="ag-product-card ag-product-card--compact">
                <div className="ag-product-media" role="img" aria-label={p.nom || p.name || ''}>
                  <img
                    src={getProductImage(p)}
                    loading="lazy"
                    alt={p.nom || p.name || ''}
                    className="ag-product-media-image"
                    onError={(event) => { handleImageError(event, p); }}
                  />
                </div>
                <div className="ag-product-body">
                  <h3 className="ag-product-name"><Link to={`/produits/${p.id}`}>{p.nom || p.name || p.title}</Link></h3>
                  <div className="ag-product-category">{p.categorie_nom || (p.categorie && (p.categorie.name || p.categorie.nom)) || p.categorie || ''}</div>
                  {promotion ? (
                    <span className="ag-badge ag-badge--warning">{`PROMO -${promotion.percent}%`}</span>
                  ) : badge ? (
                    <span className={`ag-badge ag-badge--${badge.variant}`}>{badge.text}</span>
                  ) : null}
                  <div className="ag-product-footer">
                    {promotion ? (
                      <div>
                        <div className="ag-price ag-price-sale">{`${promotion.promoPrice.toLocaleString()} FCFA`}</div>
                        <div className="ag-price-original">{`${promotion.originalPrice.toLocaleString()} FCFA`}</div>
                      </div>
                    ) : (
                      <span className="ag-price">{(p.prix_unitaire || p.price) ? `${parseInt(p.prix_unitaire || p.price).toLocaleString()} FCFA` : '—'}</span>
                    )}
                    <button
                      className="ag-btn ag-product-add"
                      type="button"
                      onClick={() => onAdd && onAdd(p, 1)}
                    >
                      + Ajouter
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="ag-home-body">
        <aside className="ag-home-sidebar">
          <div className="ag-home-panel">
            <h3>Rechercher</h3>
            <div className="ag-search ag-search--sidebar">
              <input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Rechercher maïs, engrais, pulvérisateur..."
                aria-label="Recherche produits"
              />
              {searchLoading && <div className="ag-search-status">Recherche en cours…</div>}

              {(searchEmpty || search.trim().length > 1) && (
                <ul className="ag-search-suggestions">
                  {searchEmpty ? (
                    TOP_SEARCHES.map((term) => (
                      <li key={term}>
                        <button type="button" className="ag-search-chip" onClick={() => setSearch(term)}>
                          {term}
                        </button>
                      </li>
                    ))
                  ) : filteredProduits.length > 0 ? (
                    filteredProduits.slice(0, 5).map((s) => (
                      <li key={s.id}>
                        <Link to={`/produits/${s.id}`}>{s.nom}</Link>
                      </li>
                    ))
                  ) : (
                    <li className="ag-search-empty-state">
                      <div>Aucun résultat trouvé pour « {search} ».</div>
                      <div>Essayez : {TOP_SEARCHES.slice(0, 3).map((term) => (
                        <button key={term} type="button" className="ag-search-chip" onClick={() => setSearch(term)}>{term}</button>
                      ))}</div>
                    </li>
                  )}
                </ul>
              )}
            </div>

            <div className="ag-search-hint">
              Recherchez par nom, référence, variété ou catégorie.
            </div>
          </div>

          <div className="ag-home-panel">
            <h3>Filtres</h3>
            <div className="ag-product-filters ag-product-filters--sidebar">
              <label>
                Trier par
                <select
                  value={filters.ordering}
                  onChange={(event) => setFilters((prev) => ({ ...prev, ordering: event.target.value }))}
                >
                  {SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Saison
                <select
                  value={filters.saison}
                  onChange={(event) => setFilters((prev) => ({ ...prev, saison: event.target.value }))}
                >
                  {SEASON_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </label>
              <label>
                Disponibilité
                <select
                  value={filters.disponible}
                  onChange={(event) => setFilters((prev) => ({ ...prev, disponible: event.target.value }))}
                >
                  <option value="">Tous</option>
                  <option value="true">En stock</option>
                  <option value="false">Rupture</option>
                </select>
              </label>
              <label>
                Nouveau
                <select
                  value={filters.nouveau}
                  onChange={(event) => setFilters((prev) => ({ ...prev, nouveau: event.target.value }))}
                >
                  <option value="">Tous</option>
                  <option value="true">Nouveautés</option>
                </select>
              </label>
              <label>
                Variété
                <input
                  type="text"
                  value={variete}
                  onChange={(event) => setVariete(event.target.value)}
                  placeholder="Ex: Maïs, Riz, Sésame"
                />
              </label>
              <label>
                Fourchette de prix
                <div className="ag-price-range-row">
                  <input
                    type="number"
                    value={priceRange[0]}
                    onChange={(event) => setPriceRange([Number(event.target.value) || 0, priceRange[1]])}
                    min={priceBounds[0]}
                    max={priceRange[1]}
                  />
                  <span>—</span>
                  <input
                    type="number"
                    value={priceRange[1]}
                    onChange={(event) => setPriceRange([priceRange[0], Number(event.target.value) || priceBounds[1]])}
                    min={priceRange[0]}
                    max={priceBounds[1]}
                  />
                </div>
              </label>
            </div>
          </div>

          <div className="ag-home-panel">
            <h3>Catégories</h3>
            <div className="ag-categories ag-categories--sidebar">
              {visibleCategories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  className={`ag-cat-card${filters.categorie === String(cat.id) ? ' ag-cat-card--active' : ''}`}
                  onClick={() => setFilters((prev) => ({ ...prev, categorie: prev.categorie === String(cat.id) ? '' : String(cat.id) }))}
                >
                  <div className="ag-cat-title">{(cat.icone ? `${cat.icone} ` : '') + (cat.name || cat.nom || '—')}</div>
                </button>
              ))}
            </div>
            {filters.categorie && (
              <div className="ag-filter-note ag-filter-note--sidebar">
                Filtré par catégorie : {visibleCategories.find((cat) => String(cat.id) === filters.categorie)?.nom || '—'}
                <button type="button" className="ag-btn-secondary ag-filter-clear" onClick={() => setFilters((prev) => ({ ...prev, categorie: '' }))}>
                  Effacer
                </button>
              </div>
            )}
          </div>
        </aside>

        <main className="ag-home-main">
          <div className="ag-section-header">
            <div>
              <h2>Produits en vedette</h2>
              <p>Découvrez les solutions les plus demandées par nos clients.</p>
            </div>
          </div>

          <div className="ag-products">
            {vedette.map(p => {
              const badge = getBadge(p);
              const promotion = getPromotion(p);
              const icon = getProductIcon(p);
              return (
                <article key={p.id} className={`ag-product-card ${p.en_vedette ? 'ag-product-card--featured' : ''}`}>
                  <div className="ag-product-media" role="img" aria-label={p.nom || p.name || ''}>
                    <img
                      src={getProductImage(p)}
                      loading="lazy"
                      alt={p.nom || p.name || ''}
                      className="ag-product-media-image"
                      onError={(event) => { handleImageError(event, p); }}
                    />
                  </div>
                  <div className="ag-product-card-header">
                    <div className="ag-product-avatar">{icon}</div>
                    <div className="ag-price-badge">{promotion ? `${promotion.promoPrice.toLocaleString()} FCFA` : (p.prix_unitaire || p.price) ? `${parseInt(p.prix_unitaire || p.price).toLocaleString()} FCFA` : '—'}</div>
                    {promotion ? (
                      <span className="ag-badge ag-badge--warning">{`PROMO -${promotion.percent}%`}</span>
                    ) : badge ? (
                      <span className={`ag-badge ag-badge--${badge.variant}`}>{badge.text}</span>
                    ) : null}
                  </div>
                  <div className="ag-product-body">
                    <div className="ag-product-category">{p.categorie_nom || (p.categorie && (p.categorie.name || p.categorie.nom)) || p.categorie || ''}</div>
                    <h3 className="ag-product-name"><Link to={`/produits/${p.id}`}>{p.nom || p.name || p.title}</Link></h3>
                    <p className="ag-product-desc">{p.description || p.description_courte || ''}</p>
                  </div>
                  <div className="ag-product-footer">
                    <div>
                      {promotion ? (
                        <>
                          <div className="ag-price ag-price-sale">{`${promotion.promoPrice.toLocaleString()} FCFA`}</div>
                          <div className="ag-price-original">{`${promotion.originalPrice.toLocaleString()} FCFA`}</div>
                        </>
                      ) : (
                        <div className="ag-price">{(p.prix_unitaire || p.price) ? `${parseInt(p.prix_unitaire || p.price).toLocaleString()} FCFA` : '—'}</div>
                      )}
                      <div className="ag-product-unit">{p.unite || ''}</div>
                    </div>
                    <button
                      className="ag-btn ag-product-add"
                      type="button"
                      onClick={() => onAdd && onAdd(p, 1)}
                    >
                      + Ajouter
                    </button>
                  </div>
                </article>
              );
            })}
          </div>



          <div className="ag-section-header">
            <div>
              <h2>Tous les produits</h2>
              <p>Explorez l’intégralité de notre catalogue et trouvez le matériel qui vous convient.</p>
            </div>
          </div>
          <div className="ag-products">
            {filteredProduits.length === 0 ? (
              <div className="ag-search-empty-state" style={{ padding: '24px', width: '100%' }}>
                <h3>Aucun produit ne correspond à ces critères.</h3>
                <p>Essayez d’élargir la recherche, de supprimer le filtre de prix ou de choisir une autre catégorie.</p>
              </div>
            ) : filteredProduits.map((p) => {
              const badge = getBadge(p);
              const promotion = getPromotion(p);
              const icon = getProductIcon(p);
              return (
                <article key={p.id} className={`ag-product-card ${p.en_vedette ? 'ag-product-card--featured' : ''}`}>
                  <div className="ag-product-media" role="img" aria-label={p.nom || p.name || ''}>
                    <img
                      src={getProductImage(p)}
                      loading="lazy"
                      alt={p.nom || p.name || ''}
                      className="ag-product-media-image"
                      onError={(event) => { handleImageError(event, p); }}
                    />
                  </div>
                  <div className="ag-product-card-header">
                    <div className="ag-product-avatar">{icon}</div>
                    <div className="ag-price-badge">{promotion ? `${promotion.promoPrice.toLocaleString()} FCFA` : (p.prix_unitaire || p.price) ? `${parseInt(p.prix_unitaire || p.price).toLocaleString()} FCFA` : '—'}</div>
                    {promotion ? (
                      <span className="ag-badge ag-badge--warning">{`PROMO -${promotion.percent}%`}</span>
                    ) : badge ? (
                      <span className={`ag-badge ag-badge--${badge.variant}`}>{badge.text}</span>
                    ) : null}
                  </div>
                  <div className="ag-product-body">
                    <div className="ag-product-category">{p.categorie_nom || (p.categorie && (p.categorie.name || p.categorie.nom)) || p.categorie || ''}</div>
                    <h3 className="ag-product-name"><Link to={`/produits/${p.id}`}>{p.nom || p.name || p.title}</Link></h3>
                    <p className="ag-product-desc">{p.description || p.description_courte || ''}</p>
                  </div>
                  <div className="ag-product-footer">
                    <div>
                      {promotion ? (
                        <>
                          <div className="ag-price ag-price-sale">{`${promotion.promoPrice.toLocaleString()} FCFA`}</div>
                          <div className="ag-price-original">{`${promotion.originalPrice.toLocaleString()} FCFA`}</div>
                        </>
                      ) : (
                        <div className="ag-price">{(p.prix_unitaire || p.price) ? `${parseInt(p.prix_unitaire || p.price).toLocaleString()} FCFA` : '—'}</div>
                      )}
                      <div className="ag-product-unit">{p.unite || ''}</div>
                    </div>
                    <button
                      className="ag-btn ag-product-add"
                      type="button"
                      onClick={() => onAdd && onAdd(p, 1)}
                    >
                      + Ajouter
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
          {nextPage ? (
            <div className="ag-load-more" style={{ textAlign: 'center', margin: '24px 0' }}>
              <button
                type="button"
                className="ag-btn ag-btn--secondary"
                disabled={searchLoading}
                onClick={async () => {
                  try {
                    setSearchLoading(true);
                    const nextPageNumber = page + 1;
                    const p = await loadProducts(search.trim(), filters, nextPageNumber);
                    const productsData = Array.isArray(p) ? { results: p, next: null } : p;
                    setProduits((prev) => [...prev, ...(productsData.results || [])]);
                    setPage(nextPageNumber);
                    setNextPage(productsData.next || null);
                  } catch (e) {
                    setError(getErrorMessage(e));
                  } finally {
                    setSearchLoading(false);
                  }
                }}
              >
                Charger plus de produits
              </button>
            </div>
          ) : null}
        </main>
      </section>
    </div>
  );
}
